/* istanbul ignore file */

import type { Swiper } from 'swiper/types';
import type { KeyboardOptions } from 'swiper/types/modules/keyboard';

export function elementOffset(el: HTMLElement | Window) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { top: 0, left: 0 };
  }

  if (el === window) {
    return {
      top: window.scrollY || 0,
      left: window.scrollX || 0,
    };
  }

  const box = el.getBoundingClientRect();
  const body = document.body;
  const clientTop = (el.clientTop as number) || body.clientTop || 0;
  const clientLeft = (el.clientLeft as number) || body.clientLeft || 0;

  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

  return {
    top: box.top + scrollTop - clientTop,
    left: box.left + scrollLeft - clientLeft,
  };
}

function elementParents(el: HTMLElement, selector: string) {
  const parents = []; // eslint-disable-line
  let parent = el.parentElement; // eslint-disable-line
  while (parent) {
    if (selector) {
      if (parent.matches(selector)) parents.push(parent);
    } else {
      parents.push(parent);
    }
    parent = parent.parentElement;
  }
  return parents;
}

export default function CustomKeyboard({
  swiper: swiperOriginal,
  extendParams,
  on,
  emit,
}: {
  swiper: Swiper;
  extendParams: (obj: object) => void;
  on: Swiper['on'];
  emit: Swiper['emit'];
}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const swiper = swiperOriginal as Swiper & { isVertical: () => boolean };

  swiper.keyboard = {
    enabled: false,
    enable: () => {},
    disable: () => {},
  };

  extendParams({
    keyboard: {
      enabled: false,
      onlyInViewport: true,
      pageUpDown: true,
    },
  });

  function handle(event: KeyboardEvent) {
    const activeEl = document.activeElement as HTMLElement;
    const eventTarget = event.target as HTMLElement;

    // Check if swiper is enabled
    if (!swiper.params.enabled) return;

    // Check if the active element or event target is within the carousel
    // This allows keyboard navigation when focus is on buttons or other elements inside the carousel
    const isActiveElementInCarousel = activeEl && swiper.el.contains(activeEl);
    const isEventTargetInCarousel = eventTarget && swiper.el.contains(eventTarget);

    // Only proceed if either the active element or event target is in the carousel
    // This ensures keyboard navigation works even when focus is on carousel buttons
    // Only handle events if focus is actually in THIS carousel to prevent conflicts with multiple carousels
    if (!isActiveElementInCarousel && !isEventTargetInCarousel) return;

    // If focus is clearly in this carousel, we should handle the event regardless of viewport check
    // This ensures that carousels in tabpanels (which may be hidden with display:none but still have focus)
    // can still respond to keyboard events when they become visible and receive focus
    const focusIsInThisCarousel = isActiveElementInCarousel;

    const { rtlTranslate: rtl } = swiper;
    const e = event;

    const kc = e.key;
    const pageUpDown = (swiper.params.keyboard as KeyboardOptions)?.pageUpDown;

    const isPageUp = pageUpDown && kc === 'PageUp';
    const isPageDown = pageUpDown && kc === 'PageDown';
    const isArrowLeft = kc === 'ArrowLeft';
    const isArrowRight = kc === 'ArrowRight';
    const isArrowUp = kc === 'ArrowUp';
    const isArrowDown = kc === 'ArrowDown';

    if (
      !swiper.allowSlideNext &&
      ((swiper.isHorizontal() && isArrowRight) || (swiper.isVertical() && isArrowDown) || isPageDown)
    ) {
      return false;
    }

    if (
      !swiper.allowSlidePrev &&
      ((swiper.isHorizontal() && isArrowLeft) || (swiper.isVertical() && isArrowUp) || isPageUp)
    ) {
      return false;
    }

    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return undefined;

    if (
      document.activeElement &&
      ((document.activeElement as HTMLElement).isContentEditable ||
        ['input', 'textarea'].includes(document.activeElement?.nodeName.toLowerCase()))
    ) {
      return undefined;
    }

    if (
      (swiper.params.keyboard as KeyboardOptions).onlyInViewport &&
      (isPageUp || isPageDown || isArrowLeft || isArrowRight || isArrowUp || isArrowDown)
    ) {
      // Skip viewport check if focus is clearly in this carousel
      // This allows carousels in tabpanels to work even if they're temporarily hidden
      if (!focusIsInThisCarousel) {
        let inView = false;

        if (
          elementParents(swiper.el, `.${swiper.params.slideClass}, swiper-slide`).length > 0 &&
          elementParents(swiper.el, `.${swiper.params.slideActiveClass}`).length === 0
        ) {
          return undefined;
        }

        const el = swiper.el;
        const swiperWidth = el.clientWidth;
        const swiperHeight = el.clientHeight;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const swiperOffset = elementOffset(el);
        if (rtl) swiperOffset.left -= el.scrollLeft;

        const swiperCoord = [
          [swiperOffset.left, swiperOffset.top],
          [swiperOffset.left + swiperWidth, swiperOffset.top],
          [swiperOffset.left, swiperOffset.top + swiperHeight],
          [swiperOffset.left + swiperWidth, swiperOffset.top + swiperHeight],
        ];

        for (let i = 0; i < swiperCoord.length; i += 1) {
          const [x, y] = swiperCoord[i];
          if (x >= 0 && x <= windowWidth && y >= 0 && y <= windowHeight) {
            if (x === 0 && y === 0) continue;
            inView = true;
          }
        }
        if (!inView) return undefined;
      }
    }

    (swiper.keyboard as KeyboardOptions & { lastKey?: string }).lastKey = kc;

    if (swiper.isHorizontal()) {
      if (isPageUp || isPageDown || isArrowLeft || isArrowRight) {
        e.preventDefault?.();
      }
      if (((isPageDown || isArrowRight) && !rtl) || ((isPageUp || isArrowLeft) && rtl)) swiper.slideNext();
      if (((isPageUp || isArrowLeft) && !rtl) || ((isPageDown || isArrowRight) && rtl)) swiper.slidePrev();
    } else {
      if (isPageUp || isPageDown || isArrowUp || isArrowDown) {
        e.preventDefault?.();
      }
      if (isPageDown || isArrowDown) swiper.slideNext();
      if (isPageUp || isArrowUp) swiper.slidePrev();
    }

    emit('keyPress', kc);
    return undefined;
  }

  function enable() {
    if (swiper.keyboard.enabled) return;

    swiper.el.tabIndex = swiper.el.tabIndex || 0;
    // Listen at document level to catch keyboard events from all elements within the carousel
    // This ensures keyboard navigation works when focus is on buttons or other interactive elements
    document.addEventListener('keydown', handle, true);

    swiper.keyboard.enabled = true;
  }

  function disable() {
    if (!swiper.keyboard.enabled) return;
    document.removeEventListener('keydown', handle, true);
    swiper.keyboard.enabled = false;
  }

  on('init', () => {
    if ((swiper.params.keyboard as KeyboardOptions).enabled) enable();
  });

  on('destroy', () => {
    if (swiper.keyboard.enabled) disable();
  });

  Object.assign(swiper.keyboard, { enable, disable, handle });
}
